import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Chat.css';
import { t, TranslationKey } from '../../../i18n';
import TopBar from '../../shared/TopBar';
import MainChat from './MainChat';
import InputBar from './InputBar';
import { Message, ChatProps } from './types';
import { 
  defaultWelcome, 
  scrollToBottom, 
  formatTime
} from './ChatUtils';
import { TextToSpeechService } from '../../../services/textToSpeech';
import { DialogueDispatcher } from './DialogueManagement/dispatch';
import { DialogueContext } from './DialogueManagement/types';
import { sqlApi, serializeConversationForStorage } from '../../../services/sqlApi';
import { TaskDialogueState } from './DialogueManagementTaskClassification/TaskClassificationDialogueBlueprint';
import { ConversationDialogueState } from './DialogueManagementConversation/ConversationDialogueBlueprint';

// appendMessage function that adds message to conversation
// This is the ONLY function that should add messages to conversations
const appendMessage = (
  message: Message,
  conversation: any,
  dispatcher?: DialogueDispatcher
): void => {
  // Add message to conversation
  conversation.messages.push(message);
  
  // All intent/classification is handled in the reducer. Chat only dispatches NEW_MESSAGE.
  if (dispatcher && !message.isAssistant) {
    dispatcher.dispatch({
      type: 'NEW_MESSAGE',
      message: {
        id: message.id,
        text: message.text || '',
        sender: message.sender,
        timestamp: message.timestamp,
        isAssistant: message.isAssistant,
        messageType: message.messageType || 'user',
        processed: message.processed || false,
        taskData: message.taskData,
        textKey: message.textKey,
        // Preserve structured selection metadata for the reducer (no text parsing in Chat.tsx)
        taskId: message.taskId,
        taskDescription: message.taskDescription,
        parentId: message.parentId,
        selectedTaskId: message.selectedTaskId,
        selectedTaskIds: message.selectedTaskIds,
        variantId: (message as any).variantId,
        selectedVariantId: message.selectedVariantId,
        parentTaskId: message.parentTaskId
      }
    }).catch(error => {
      console.error('Error dispatching NEW_MESSAGE:', error);
    });
    return;
  }
};


const Chat: React.FC<ChatProps> = ({
  currentUser,
  lang,
  setLang,
  currentConversation,
  onNewConversationRequested,
  triggerConversationUpdate,
  setConversations,
  onLogout,
}) => {

  const [newMessage, setNewMessage] = useState('');
  const [textToSpeechEnabled, setTextToSpeechEnabled] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dispatcherRef = useRef<DialogueDispatcher | null>(null);
  const previousConversationStateRef = useRef<ConversationDialogueState | undefined>(undefined);
  const processedConversationIdsRef = useRef<Set<string>>(new Set());
  const processNextPendingTaskRef = useRef<(() => Promise<void>) | null>(null);

  // Initialize dispatcher when conversation ID changes (only recreate when conversation actually changes)
  // IMPORTANT: Don't create dispatcher for submitted conversations - they're just for viewing
  useEffect(() => {
    if (currentConversation) {
      // Skip dispatcher creation for submitted conversations - no dialogue management needed
      if (currentConversation.isSubmitted) {
        console.log('📋 Conversation is submitted, skipping dispatcher creation');
      dispatcherRef.current = null;
        return;
      }
      
      const conversationId = currentConversation.id;
      const currentDispatcher = dispatcherRef.current;
      
      // Only create new dispatcher if conversation ID changed or dispatcher doesn't exist
      const needsNewDispatcher = !currentDispatcher || 
        currentDispatcher.getCurrentState().context.conversationId !== conversationId;
      
      if (needsNewDispatcher) {
        // Preserve taskState from existing dispatcher if conversation ID matches
        let preservedTaskState: TaskDialogueState | undefined;
        let preservedTaskContext: any | undefined;
        
        if (currentDispatcher) {
          const prevState = currentDispatcher.getCurrentState();
          if (prevState.context.conversationId === conversationId) {
            preservedTaskState = prevState.context.taskState;
            preservedTaskContext = prevState.context.taskContext;
          }
        }
      
      const initialContext: DialogueContext = {
        slots: {},
        language: lang,
        userId: currentUser,
          conversationId: conversationId,
          taskState: preservedTaskState,
          taskContext: preservedTaskContext
      };
      
      dispatcherRef.current = new DialogueDispatcher(
        currentConversation.conversationDialogueState || 'conv_welcome',
        initialContext,
        currentConversation,
        triggerConversationUpdate,
        () => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        },
        undefined,
        onNewConversationRequested,
        currentUser,
        () => {
          // Callback when a task completes - process next pending task
          if (processNextPendingTaskRef.current) {
            processNextPendingTaskRef.current();
          }
        }
      );
      }
    }
    // Only depend on conversation ID - other updates handled separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.id]);

  // Update language in dispatcher when language changes
  useEffect(() => {
    if (dispatcherRef.current) {
      dispatcherRef.current.updateLanguage(lang);
    }
  }, [lang]);

  // Watch for conv_bye state and trigger new conversation (only on fresh transitions)
  useEffect(() => {
    if (!currentConversation || !dispatcherRef.current) return;
    
    // Check both dispatcher state (source of truth) and conversation object state
    const dispatcherState = dispatcherRef.current.getCurrentState();
    const dispatcherConversationState = dispatcherState.conversationState;
    const conversationState = currentConversation.conversationDialogueState || dispatcherConversationState;
    const conversationId = currentConversation.id;
    const isSubmitted = currentConversation.isSubmitted;
    const processedIds = processedConversationIdsRef.current;
    
    // Initialize tracking when we first see a conversation
    if (conversationId && !processedIds.has(conversationId + '_initialized')) {
      processedIds.add(conversationId + '_initialized');
      
      // If it's already in conv_bye when we first see it, mark it as "don't trigger"
      // This means it's an old conversation that was loaded
      if (conversationState === 'conv_bye') {
        processedIds.add(conversationId + '_no_trigger');
        console.log('📋 Loaded old conversation already in conv_bye state, marking as no-trigger');
        // Set previous state to current state to prevent triggering
        previousConversationStateRef.current = conversationState;
        return;
      }
      
      // Set previous state to current state when first loading a fresh conversation
      previousConversationStateRef.current = conversationState;
      return;
    }
    
    // Skip if this conversation was marked as "no trigger" (old conversation loaded in conv_bye)
    if (processedIds.has(conversationId + '_no_trigger')) {
      return;
    }
    
    // Skip if we've already triggered a new conversation for this conversation ID
    if (processedIds.has(conversationId + '_new_conversation_triggered')) {
      return;
    }
    
    // Check for transition: read previous state BEFORE updating it
    const previousState = previousConversationStateRef.current;
    
    // Only trigger if:
    // 1. We're in conv_bye state (check both dispatcher and conversation object)
    // 2. Conversation is submitted
    // 3. We just transitioned TO conv_bye (previous state was not conv_bye)
    const isInConvBye = conversationState === 'conv_bye' || dispatcherConversationState === 'conv_bye';
    const isFreshTransition = previousState !== undefined && 
                              previousState !== 'conv_bye' && 
                              isInConvBye;
    
    // Only log when we're actually checking for conv_bye transition
    if (isInConvBye || previousState === 'conv_bye') {
      console.log('🔍 Checking conv_bye transition:', {
        previousState,
        conversationState,
        dispatcherConversationState,
        isSubmitted,
        conversationId,
        isInConvBye,
        isFreshTransition
      });
    }
    
    // Update previous state ref AFTER checking for transition
    if (previousState !== conversationState && previousState !== dispatcherConversationState) {
      previousConversationStateRef.current = conversationState || dispatcherConversationState;
    }
    
    if (isFreshTransition && isSubmitted) {
      console.log('🔄 Conversation freshly transitioned to conv_bye and is submitted, triggering new conversation');
      console.log('📊 Transition details:', {
        previousState,
        currentState: conversationState,
        dispatcherState: dispatcherConversationState,
        conversationId,
        isSubmitted
      });
      processedIds.add(conversationId + '_new_conversation_triggered');
      
      // Use setTimeout to ensure the current state updates are processed first
      const timeoutId = setTimeout(() => {
        onNewConversationRequested();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentConversation?.conversationDialogueState, currentConversation?.isSubmitted, currentConversation?.id, onNewConversationRequested, triggerConversationUpdate]);

  // Server stream handler - for incoming messages from server/WebSocket/SSE
  const handleServerMessage = (
    serverPayload: any,
    conversation: any
  ): void => {
    // Map server payload to Message shape
    const message: Message = {
      id: serverPayload.id || Math.random().toString(36).substr(2, 9),
      text: serverPayload.text || '',
      sender: serverPayload.sender || 'Assistant',
      timestamp: new Date(serverPayload.timestamp || Date.now()),
      isAssistant: serverPayload.isAssistant !== false,
      messageType: serverPayload.messageType || 'assistant',
      processed: false
    };

    // De-dup by id (check if message already exists)
    const existingMessage = conversation.messages.find((msg: Message) => msg.id === message.id);
    if (existingMessage) {
      console.log('Duplicate message detected, skipping:', message.id);
      return;
    }

    // Use appendMessage to add the server message
    appendMessage(message, conversation, dispatcherRef.current || undefined);
  };

  // MESSAGE FLOW CONTROL:
  // appendMessage is ONLY called from these sources:
  // 1. User inputs (send button, enter key, quick-replies, file/drop)
  // 2. Server stream (WebSocket/SSE/polling)
  //
  // appendMessage adds the message AND dispatches NEW_MESSAGE event
  // NOT from: useEffect watching messages, processMessage, timers, etc.

  const messages = currentConversation ? currentConversation.messages : defaultWelcome;

  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [messages]);

  useEffect(() => {
    TextToSpeechService.textToSpeechEnabled = textToSpeechEnabled;
    TextToSpeechService.currentLanguage = lang;
  }, [textToSpeechEnabled, lang]);

  useEffect(() => {
    if (currentConversation && currentConversation.messages) {
      // Update all assistant messages with textKey when language changes
      currentConversation.messages.forEach((message: any) => {
        if (message.isAssistant && message.textKey) {
          message.text = t(lang, message.textKey);
        }
      });
      // Force re-render by updating state
      setForceUpdate(prev => prev + 1);
    }
  }, [lang, currentConversation]);


  const handleSendMessageWrapper = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const trimmed = newMessage.trim();

    const pushUserMessageDirectly = (processed = true): Message | null => {
      if (!currentConversation) {
        return null;
      }
      const directMessage: Message = {
        id: Math.random().toString(36).substr(2, 9),
        text: trimmed,
        sender: 'User',
        timestamp: new Date(),
        isAssistant: false,
        messageType: 'user',
        processed,
      };
      currentConversation.messages.push(directMessage);
      triggerConversationUpdate();
      return directMessage;
    };

    // Check if conversation is already submitted
    if (currentConversation?.isSubmitted) {
      console.log("📝 Adding comment to submitted conversation");
      
      // Add the message as a comment (no task processing)
      const commentMessage: Message = {
        id: Math.random().toString(36).substr(2, 9),
        text: trimmed,
        sender: 'User',
        timestamp: new Date(),
        isAssistant: false,
        messageType: 'user',
        processed: true, // Mark as processed to prevent task processing
      };
      appendMessage(commentMessage, currentConversation, dispatcherRef.current || undefined);
      setNewMessage('');

      // Persist updated conversation (with new comment) to SQL
      const savedId = currentConversation.savedConversationId;
      if (savedId != null) {
        try {
          const conversationPayload = serializeConversationForStorage(currentConversation);
          await sqlApi.updateConversation(savedId, conversationPayload);
        } catch (err) {
          console.warn('Failed to update conversation in database:', err);
        }
      }
      return;
    }
        
    if (!currentConversation) {
      console.error('No conversation selected');
          return;
        }

    if (trimmed.toUpperCase() === 'SUBMIT' || trimmed.startsWith('!')) {
      // Echo the command in the chat without triggering further processing
      pushUserMessageDirectly(true);

      currentConversation.markAsSubmitted();
      
      // Request new conversation from parent
      onNewConversationRequested();
      setNewMessage('');
      return;
    }

    // Handle pending abort confirmation responses
    if (currentConversation.abortConfirmationPending) {
      pushUserMessageDirectly(true);

      const positiveAbortPatterns = /^(yes|yep|yeah|y|ok|okay|confirm|sure|cancel|stop|abort|ja|jep|jazeker|akkoord|prima|goed|annuleer|annuleren|afbreken)$/i;
      const negativeAbortPatterns = /^(no|nope|nah|nee|niet|continue|doorgaan|ga door|blijf doorgaan|keep going)$/i;

      if (positiveAbortPatterns.test(trimmed)) {
        currentConversation.markAsCancelled();
        currentConversation.addSystemMessage(t(lang, 'abortConfirmed'), 'abortConfirmed');
        currentConversation.clearAbortConfirmation();
        triggerConversationUpdate();
        onNewConversationRequested();
        setNewMessage('');
        return;
      }

      if (negativeAbortPatterns.test(trimmed)) {
        currentConversation.addSystemMessage(t(lang, 'abortCancelled'), 'abortCancelled');
        
        // Restore the previous state and trigger entry actions to re-render widgets
        const returnState = currentConversation.abortReturnState;
        if (returnState && dispatcherRef.current) {
          // Restore conversation state
          currentConversation.updateDialogueState(returnState);
          
          // Update dispatcher state to match
          dispatcherRef.current.updateState(returnState);
          
          // Manually trigger entry actions to re-render widgets
          // We need to dispatch a system event that will trigger entry actions
          setTimeout(async () => {
            const { getConversationStateMetadata } = await import('./DialogueManagementConversation/ConversationDialogueBlueprint');
            const metadata = getConversationStateMetadata(returnState);
            
            // For defined task classification, we need to trigger the entry action with wellDefinedTaskId
            // to re-trigger subtask selection widgets
            if (!dispatcherRef.current) {
              triggerConversationUpdate();
              return;
            }
            
            const effectBus = dispatcherRef.current.getEffectBus();
            
            if (returnState === 'conv_defined_task_classification') {
              // Get wellDefinedTaskId from dispatcher context or from tasks
              const dispatcherState = dispatcherRef.current.getCurrentState();
              const wellDefinedTaskId = dispatcherState.context.wellDefinedTaskId || 
                currentConversation.tasks?.find((t: any) => t.wellDefinedTaskId)?.wellDefinedTaskId;
              
              // Trigger the entry action with wellDefinedTaskId parameter
              if (metadata.entryActionKeys && metadata.entryActionKeys.length > 0) {
                for (const actionKey of metadata.entryActionKeys) {
                  await effectBus.runEffects([{
                    type: 'RUN_ENTRY_ACTION',
                    key: actionKey,
                    params: wellDefinedTaskId ? { wellDefinedTaskId } : undefined
                  }]);
                }
              }
            } else {
              // For other states, trigger entry actions through the dispatcher's effect bus
              if (metadata.entryActionKeys && metadata.entryActionKeys.length > 0) {
                for (const actionKey of metadata.entryActionKeys) {
                  await effectBus.runEffects([{
                    type: 'RUN_ENTRY_ACTION',
                    key: actionKey,
                    params: undefined
                  }]);
                }
              } else if (metadata.entryPromptKey) {
                // If there's an entry prompt but no entry actions, just add the message
                currentConversation.addSystemMessage(t(lang, metadata.entryPromptKey as TranslationKey), metadata.entryPromptKey as TranslationKey);
              }
            }
            
            triggerConversationUpdate();
          }, 100);
        } else if (currentConversation.abortReturnMessage) {
          // Fallback: just reprint the message if we can't restore state
          const { text, textKey } = currentConversation.abortReturnMessage;
          if (textKey) {
            currentConversation.addSystemMessage(t(lang, textKey as TranslationKey), textKey as TranslationKey);
          } else if (text) {
            currentConversation.addSystemMessage(text);
          }
        }
        
        currentConversation.clearAbortConfirmation();
        triggerConversationUpdate();
        setNewMessage('');
        return;
      }

      currentConversation.addSystemMessage(t(lang, 'abort_confirmation'), 'abort_confirmation');
      triggerConversationUpdate();
      setNewMessage('');
      return;
    }

    // Detect new abort initiation keywords
    const abortPatterns = /^(abort|stop|cancel|annuleer|annuleren|afbreken|afbreek|break|terminate|exit|quit)$/i;
    if (abortPatterns.test(trimmed)) {
      pushUserMessageDirectly(true);
      currentConversation.startAbortConfirmation(currentConversation.conversationDialogueState);
      currentConversation.addSystemMessage(t(lang, 'abort_confirmation'), 'abort_confirmation');
      triggerConversationUpdate();
      setNewMessage('');
      return;
    }

    // Add user message using appendMessage
    const userMessage: Message = {
                id: Math.random().toString(36).substr(2, 9),
      text: trimmed,
      sender: 'User',
                timestamp: new Date(),
      isAssistant: false,
      messageType: 'user',
                processed: false
              };
              
    appendMessage(userMessage, currentConversation, dispatcherRef.current || undefined);
    setNewMessage('');
  };

  const handleSelectTaskWrapper = async (messageId: string, taskId: string | null) => {
    if (!currentConversation || !taskId) return;

    // Find the task message to get the description
    const taskMessage = currentConversation.messages.find((msg: Message) => 
      msg.messageType === 'taskSelection' && 
      msg.text && 
      (msg.text.includes(`Task ID: ${taskId}`) || msg.text.includes(`Taak ID: ${taskId}`) || msg.taskId === taskId)
    );
    
    if (taskMessage) {
      const selectedDescription = taskMessage.taskDescription || taskMessage.text || '';
      
      // Check if we're in option_selection state (for subtask processing)
      // We need to check multiple sources:
      // 1. Task object's dialogueState (if active task exists)
      // 2. Dispatcher's context.definedTaskState (more reliable)
      // 3. Presence of taskId in the message (indicates option selection)
      const activeTask = currentConversation.tasks?.find((t: any) => t.isProcessingDialogue);
      const taskDialogueState = activeTask?.dialogueState;
      
      // Get state from dispatcher context (more reliable)
      let dispatcherDefinedTaskState: string | undefined;
      if (dispatcherRef.current) {
        const dispatcherState = dispatcherRef.current.getCurrentState();
        dispatcherDefinedTaskState = dispatcherState.context.definedTaskState;
      }
      
      // Check if we're in option_selection from any source
      const isInOptionSelection = 
        taskDialogueState === 'option_selection' || 
        dispatcherDefinedTaskState === 'option_selection' ||
        (dispatcherRef.current && currentConversation.conversationDialogueState === 'conv_defined_task_classification' && !!taskId);
      
      // DEBUG: Log state detection
      console.log('🔍 handleSelectTaskWrapper - State check:', {
        hasActiveTask: !!activeTask,
        activeTaskDialogueState: taskDialogueState,
        dispatcherDefinedTaskState,
        conversationState: currentConversation.conversationDialogueState,
        isInOptionSelection,
        hasDispatcher: !!dispatcherRef.current,
        taskId,
        hasTaskId: !!taskId
      });
      
      // If we're in option_selection OR we have a taskId (option selection), handle it directly without LLM
      if ((isInOptionSelection || taskId) && dispatcherRef.current && currentConversation.conversationDialogueState === 'conv_defined_task_classification') {
        // Print the selected task object
        console.log('🔍 OPTION SELECTED - Full task object:', JSON.stringify({
          taskId: taskId,
          taskMessage: taskMessage,
          taskDescription: selectedDescription,
          activeTask: activeTask
        }, null, 2));
        
        // Create user message for display
        const userMessage: Message = {
          id: Math.random().toString(36).substr(2, 9),
          text: selectedDescription,
          sender: 'User',
          timestamp: new Date(),
          isAssistant: false,
          messageType: 'user',
          processed: false,
          taskId: taskId // Store task ID for option selection
        };

        // Add user message to conversation for display
        currentConversation.messages.push(userMessage);
        
        // Remove task selection messages
        currentConversation.messages = currentConversation.messages.filter((msg: Message) => 
          msg.messageType !== 'taskSelection'
        );

        // Dispatch NEW_MESSAGE event directly (the reducer will handle it and bypass LLM)
        dispatcherRef.current.dispatch({
          type: 'NEW_MESSAGE',
          message: userMessage
        }).catch(error => {
          console.error('Error dispatching NEW_MESSAGE for option selection:', error);
        });

        // Ensure UI updates immediately
        if (typeof triggerConversationUpdate === 'function') {
          triggerConversationUpdate();
        }
        
        return; // Do not fall through to normal message handling
      }
      
      if (selectedDescription) {
        // Create user message for the selected task
        // Include task ID in the message if we're selecting an option (for recursive validation)
        // Use i18n format for consistency
        const messageText = isInOptionSelection 
          ? t(lang, 'taskIdDescription', { taskId: taskId, description: selectedDescription })
          : selectedDescription;
        
        const userMessage: Message = {
          id: Math.random().toString(36).substr(2, 9),
          text: messageText,
          sender: 'User',
          timestamp: new Date(),
          isAssistant: false,
          messageType: 'user',
          processed: false,
          taskId: isInOptionSelection ? taskId : undefined // Store task ID for option selection
        };

        // Use appendMessage to add the user message
        appendMessage(userMessage, currentConversation, dispatcherRef.current || undefined);
        
        // Remove task selection messages
        currentConversation.messages = currentConversation.messages.filter((msg: Message) => 
          msg.messageType !== 'taskSelection'
        );

        // Ensure UI updates immediately after removing the option cards
        if (typeof triggerConversationUpdate === 'function') {
          triggerConversationUpdate();
        }
      }
    }
  };

  // Handler for multi-select mode: update selectedTaskIds array in the message
  const handleSelectMultipleTasksWrapper = (messageId: string, taskIds: string[]) => {
    if (!currentConversation) return;
    
    // Find the parent message (the prompt message with id === messageId)
    const parentMessage = currentConversation.messages.find(
      (msg: Message) => msg.id === messageId
    );
    
    if (parentMessage) {
      // Update the parent message with selectedTaskIds
      onUpdateMessage(messageId, {
        selectedTaskIds: taskIds
      });
      
      // Trigger UI update
      if (typeof triggerConversationUpdate === 'function') {
        triggerConversationUpdate();
      }
    }
  };

  // Helper function to process the next unstarted task from the queue
  // Uses the same pattern as returnActiveTask - finds the first unstarted task and processes it
  const processNextPendingTask = useCallback(async () => {
    console.log('🔄 [processNextPendingTask] ========== START ==========');
    console.log('🔄 [processNextPendingTask] Function called', {
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n')
    });
    
    if (!currentConversation) {
      console.warn('🔄 [processNextPendingTask] BLOCKED - Missing currentConversation');
      return;
    }
    
    if (!dispatcherRef.current) {
      console.warn('🔄 [processNextPendingTask] BLOCKED - Missing dispatcherRef.current');
      return;
    }
    
    console.log('🔄 [processNextPendingTask] Prerequisites met - proceeding');

    // Log all tasks for debugging
    const allTasks = currentConversation.tasks || [];
    const wellDefinedTasks = allTasks.filter((t: any) => t.wellDefinedTaskId);
    console.log('🔄 [processNextPendingTask] All tasks:', allTasks.length, 'Well-defined tasks:', wellDefinedTasks.length);
    wellDefinedTasks.forEach((t: any, idx: number) => {
      console.log(`  Task ${idx + 1}:`, {
        id: t.id,
        taskId: t.taskId,
        userDescription: t.userDescription,
        dialogueState: t.dialogueState,
        isProcessingDialogue: t.isProcessingDialogue,
        isSubmitted: t.isSubmitted,
        wellDefinedTaskId: t.wellDefinedTaskId
      });
    });

    // Find the first unstarted task (same logic as returnActiveTask)
    // CRITICAL: Process tasks in the order they were created (by original taskId)
    // This ensures subtask 1 is fully processed before subtask 2 starts
    // Only look for well-defined task objects (from multi-select)
    // CRITICAL: Exclude tasks in 'option_selection' state - these are parent tasks that should be removed
    // or tasks that are waiting for user selection (not ready to process)
    const eligibleTasks = currentConversation.tasks?.filter((t: any) => 
      !t.isSubmitted && 
      t.dialogueState !== 'defined_task_completed' && 
      t.dialogueState !== 'task_completed' &&
      t.dialogueState !== 'subtask_completed' && // Added per SubtaskClassificationDialogueBlueprint
      t.dialogueState !== 'defined_task_aborted' &&
      t.dialogueState !== 'task_aborted' &&
      t.dialogueState !== 'subtask_aborted' && // Added per SubtaskClassificationDialogueBlueprint
      t.dialogueState !== 'option_selection' && // Exclude option_selection tasks - they're not ready
      !t.isProcessingDialogue &&
      t.wellDefinedTaskId // Only process well-defined task objects (from multi-select)
    ) || [];
    
    // CRITICAL: Sort by taskId to ensure sequential processing (141, 142, 143, etc.)
    // This ensures subtask 1 is fully processed before subtask 2 starts
    eligibleTasks.sort((a: any, b: any) => {
      const idA = parseFloat(a.taskId) || 0;
      const idB = parseFloat(b.taskId) || 0;
      return idA - idB;
    });
    
    const unstartedTask = eligibleTasks[0]; // Get first task after sorting
    
    console.log('🔄 [processNextPendingTask] ========== SEARCHING FOR NEXT TASK ==========');
    console.log('🔄 [processNextPendingTask] Searching for unstarted task', {
      totalTasks: currentConversation.tasks?.length || 0,
      tasksStatus: currentConversation.tasks?.map((t: any) => ({
        id: t.id,
        taskId: t.taskId,
        dialogueState: t.dialogueState,
        isProcessingDialogue: t.isProcessingDialogue,
        isSubmitted: t.isSubmitted,
        wellDefinedTaskId: t.wellDefinedTaskId,
        isEligible: !t.isSubmitted && 
          t.dialogueState !== 'defined_task_completed' && 
          t.dialogueState !== 'task_completed' &&
          t.dialogueState !== 'defined_task_aborted' &&
          t.dialogueState !== 'task_aborted' &&
          t.dialogueState !== 'option_selection' &&
          !t.isProcessingDialogue &&
          !!t.wellDefinedTaskId
      })) || []
    });

    if (unstartedTask) {
      console.log('🔄 [processNextPendingTask] ✅ FOUND UNSTARTED TASK:', {
        id: unstartedTask.id,
        taskId: unstartedTask.taskId,
        dialogueState: unstartedTask.dialogueState,
        isProcessingDialogue: unstartedTask.isProcessingDialogue,
        wellDefinedTaskId: unstartedTask.wellDefinedTaskId
      });
    } else {
      console.log('🔄 [processNextPendingTask] ❌ NO UNSTARTED TASK FOUND');
      console.log('🔄 [processNextPendingTask] Detailed task analysis:', {
        totalTasks: currentConversation.tasks?.length || 0,
        allTasks: currentConversation.tasks?.map((t: any) => {
          const isEligible = !t.isSubmitted && 
            t.dialogueState !== 'defined_task_completed' && 
            t.dialogueState !== 'task_completed' &&
            t.dialogueState !== 'subtask_completed' && // Added per SubtaskClassificationDialogueBlueprint
            t.dialogueState !== 'defined_task_aborted' &&
            t.dialogueState !== 'task_aborted' &&
            t.dialogueState !== 'subtask_aborted' && // Added per SubtaskClassificationDialogueBlueprint
            t.dialogueState !== 'option_selection' &&
            !t.isProcessingDialogue &&
            !!t.wellDefinedTaskId;
          
          return {
            id: t.id,
            taskId: t.taskId,
            dialogueState: t.dialogueState,
            isProcessingDialogue: t.isProcessingDialogue,
            isSubmitted: t.isSubmitted,
            wellDefinedTaskId: t.wellDefinedTaskId,
            isEligible,
            reasonNotEligible: !isEligible ? (
              t.isSubmitted ? 'isSubmitted' :
              t.dialogueState === 'defined_task_completed' ? 'defined_task_completed' :
              t.dialogueState === 'task_completed' ? 'task_completed' :
              t.dialogueState === 'subtask_completed' ? 'subtask_completed' :
              t.dialogueState === 'defined_task_aborted' ? 'defined_task_aborted' :
              t.dialogueState === 'task_aborted' ? 'task_aborted' :
              t.dialogueState === 'subtask_aborted' ? 'subtask_aborted' :
              t.dialogueState === 'option_selection' ? 'option_selection' :
              t.isProcessingDialogue ? 'isProcessingDialogue' :
              !t.wellDefinedTaskId ? 'no wellDefinedTaskId' :
              'unknown'
            ) : 'ELIGIBLE'
          };
        }) || []
      });
      
      // No more unstarted tasks - check if all tasks are completed
      // CRITICAL: option_selection tasks are parent tasks that should have been removed when children were created
      // If they're still there, they're likely orphaned and shouldn't block completion
      // So we check if all non-option_selection tasks are completed
      const allTasksCompleted = currentConversation.tasks?.every((t: any) => 
        !t.wellDefinedTaskId || // Ignore parent tasks without wellDefinedTaskId
        t.dialogueState === 'option_selection' || // option_selection tasks are parent tasks waiting for user - don't block completion
        t.dialogueState === 'defined_task_completed' || 
        t.dialogueState === 'task_completed' ||
        t.dialogueState === 'subtask_completed' || // Added per SubtaskClassificationDialogueBlueprint
        t.isSubmitted
      );
      
      console.log('🔄 [processNextPendingTask] All tasks completed?', allTasksCompleted, {
        totalTasks: currentConversation.tasks?.length || 0,
        wellDefinedCount: wellDefinedTasks.length,
        wellDefinedTasksStatus: wellDefinedTasks.map((t: any) => ({
          taskId: t.taskId,
          dialogueState: t.dialogueState,
          isSubmitted: t.isSubmitted,
          note: t.dialogueState === 'option_selection' ? 'parent task (should have been removed)' : ''
        }))
      });
      
      if (allTasksCompleted && currentConversation.tasks && currentConversation.tasks.length > 0) {
        // All tasks completed - check if location has been asked/collected
        const dispatcherState = dispatcherRef.current.getCurrentState();
        const definedTaskState = dispatcherState.context.definedTaskState;
        const hasLocation = dispatcherState.context.slots?.location;
        
        console.log('🔄 [processNextPendingTask] Checking location state:', {
          definedTaskState,
          hasLocation,
          conversationState: dispatcherState.conversationState
        });
        
        if (dispatcherState.conversationState === 'conv_defined_task_classification') {
          // Blueprint: subtasks_processing → ALL_SUBTASKS_PROCESSED → defined_task_completed (location from assignment)
          // When all tasks are defined_task_completed and slots.location is set, ALL_TASKS_COMPLETED → final_confirmation
          const allTasksDefinedCompleted = currentConversation.tasks?.filter((t: any) => t.wellDefinedTaskId).every((t: any) => 
            t.dialogueState === 'defined_task_completed' || 
            t.dialogueState === 'option_selection' // option_selection tasks are parent tasks that should have been removed
          );
          
          console.log('🔄 [processNextPendingTask] Checking if all tasks are defined_task_completed:', {
            allTasksDefinedCompleted,
            definedTaskState,
            hasLocation,
            tasksStatus: currentConversation.tasks?.filter((t: any) => t.wellDefinedTaskId).map((t: any) => ({
              taskId: t.taskId,
              dialogueState: t.dialogueState,
              isSubmitted: t.isSubmitted
            }))
          });
          
          if (allTasksDefinedCompleted && hasLocation) {
            // All tasks are defined_task_completed and location is collected - follow blueprint: dispatch ALL_TASKS_COMPLETED
            // Blueprint: conv_defined_task_classification → ALL_TASKS_COMPLETED → final_confirmation (special handling for well-defined tasks)
            console.log('🔄 [processNextPendingTask] All tasks are defined_task_completed and location collected, following blueprint: dispatching ALL_TASKS_COMPLETED → final_confirmation');
            const context = dispatcherState.context;
            context.definedTaskState = undefined;
            context.subtaskData = undefined;
            context.wellDefinedTaskId = undefined;
            
            await dispatcherRef.current.dispatch({
              type: 'PARSE_RESULT',
              intentId: 'ALL_TASKS_COMPLETED',
              slots: {},
              confidence: 1.0,
              messageId: Math.random().toString(36).substr(2, 9)
            }).catch(error => {
              console.error('Error dispatching ALL_TASKS_COMPLETED (following blueprint):', error);
            });
          } else if (!hasLocation && !allTasksDefinedCompleted) {
            // After the last subtask amount, context.definedTaskState is often still "amount_collection"
            // (not "subtasks_processing"). Apply assignment location whenever all subtasks are done.
            console.log(
              '🔄 [processNextPendingTask] All subtasks done — ALL_SUBTASKS_PROCESSED (assignment location), definedTaskState:',
              definedTaskState
            );
            await dispatcherRef.current
              .dispatch({
                type: 'PARSE_RESULT',
                intentId: 'ALL_SUBTASKS_PROCESSED',
                slots: {},
                confidence: 1.0,
                messageId: Math.random().toString(36).substr(2, 9),
              })
              .catch((error) => {
                console.error('Error dispatching ALL_SUBTASKS_PROCESSED:', error);
              });
          } else {
            console.log('🔄 [processNextPendingTask] Waiting for defined-task / location (unexpected branch)', {
              definedTaskState,
              hasLocation,
              allTasksDefinedCompleted,
            });
          }
        }
      } else {
        console.log('🔄 [processNextPendingTask] ⚠️ Not all tasks completed, but no unstarted task found. This might indicate a state issue.');
        console.log('🔄 [processNextPendingTask] This could mean:');
        console.log('🔄 [processNextPendingTask] - Tasks are in intermediate states (not terminal, not ready)');
        console.log('🔄 [processNextPendingTask] - Tasks are in option_selection (waiting for user)');
        console.log('🔄 [processNextPendingTask] - Tasks are marked as processing but not actually processing');
      }
      console.log('🔄 [processNextPendingTask] ========== END (NO TASK FOUND) ==========');
      return;
    }

    console.log('🔄 [processNextPendingTask] ✅ FOUND UNSTARTED TASK - Will process it:', { 
      taskId: unstartedTask.taskId, 
      description: unstartedTask.userDescription,
      id: unstartedTask.id,
      dialogueState: unstartedTask.dialogueState,
      wellDefinedTaskId: unstartedTask.wellDefinedTaskId
    });

    // Mark task as processing
    unstartedTask.isProcessingDialogue = true;
    
    // CRITICAL: Check if this is a new subtask starting (different from the previous one)
    // Find the last completed task to see if we're moving to a new subtask
    const completedTasks = currentConversation.tasks?.filter((t: any) => 
      t.wellDefinedTaskId === unstartedTask.wellDefinedTaskId &&
      (t.dialogueState === 'subtask_completed' || t.isSubmitted)
    ) || [];
    
    const isNewSubtask = completedTasks.length > 0; // If there are completed tasks, this is a new subtask
    
    console.log('🔄 [processNextPendingTask] Starting new subtask', {
      taskId: unstartedTask.taskId,
      description: unstartedTask.userDescription,
      isNewSubtask,
      completedCount: completedTasks.length,
      completedTaskIds: completedTasks.map((t: any) => t.taskId)
    });

    // Create user message for display
    const userMessage: Message = {
      id: Math.random().toString(36).substr(2, 9),
      text: unstartedTask.userDescription || '',
      sender: 'User',
      timestamp: new Date(),
      isAssistant: false,
      messageType: 'user',
      processed: false,
      taskId: unstartedTask.taskId,
      taskDescription: unstartedTask.userDescription,
      isNewSubtask: isNewSubtask // Flag to indicate this is a new subtask starting
    };

    // Add user message to conversation
    currentConversation.messages.push(userMessage);

    // Trigger UI update
    if (typeof triggerConversationUpdate === 'function') {
      triggerConversationUpdate();
    }

    // Dispatch NEW_MESSAGE to start processing this task
    // The reducer will handle it and trigger CHECK_SUBTASK_LEAF
    try {
      await dispatcherRef.current.dispatch({
        type: 'NEW_MESSAGE',
        message: userMessage
      });
      console.log('🔄 [processNextPendingTask] ✅ Successfully started processing task', unstartedTask.taskId);
      console.log('🔄 [processNextPendingTask] ========== END (SUCCESS) ==========');
    } catch (error) {
      console.error('🔄 [processNextPendingTask] ❌ ERROR dispatching NEW_MESSAGE for task processing:', error);
      console.log('🔄 [processNextPendingTask] ========== END (ERROR) ==========');
    }
  }, [currentConversation, triggerConversationUpdate]);
  
  // Store the callback in a ref so it's always accessible
  processNextPendingTaskRef.current = processNextPendingTask;

  // Handler for Continue button: create all task objects at once, then process them sequentially
  const handleContinueSelectionWrapper = async (messageId: string, selectedTaskIds: string[]) => {
    console.log('🟥 [handleContinueSelectionWrapper] ========== INDienen BUTTON FLOW START ==========', {
      messageId, 
      selectedTaskIds, 
      selectedCount: selectedTaskIds.length,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack
    });
    console.log('🔵 [handleContinueSelectionWrapper] ENTRY', { 
      messageId, 
      selectedTaskIds, 
      selectedCount: selectedTaskIds.length,
      timestamp: new Date().toISOString()
    });
    
    if (!currentConversation || !dispatcherRef.current || selectedTaskIds.length === 0) {
      console.warn('🔵 [handleContinueSelectionWrapper] Missing requirements', { 
        hasConversation: !!currentConversation, 
        hasDispatcher: !!dispatcherRef.current, 
        selectedCount: selectedTaskIds.length 
      });
      return;
    }

    // ✅ REGULAR TASK PROCESSING (non well-defined tasks)
    // If we're NOT in conv_defined_task_classification, treat this as the classic
    // task-processing flow: single choice confirmed via the button.
    if (currentConversation.conversationDialogueState !== 'conv_defined_task_classification') {
      const firstSelectedId = selectedTaskIds[0];

      // Find the task selection message corresponding to the first selected id
      const taskSelectionMessages = currentConversation.messages.filter(
        (msg: Message) =>
          msg.messageType === 'taskSelection' && msg.parentId === messageId
      );

      const taskMessage = taskSelectionMessages.find(
        (msg: Message) =>
          msg.taskId === firstSelectedId ||
          (msg.text &&
            (msg.text.includes(`Task ID: ${firstSelectedId}`) ||
             msg.text.includes(`Taak ID: ${firstSelectedId}`)))
      );

      if (!taskMessage) {
        console.warn('🔵 [handleContinueSelectionWrapper] No matching taskSelection message found for regular task processing', {
          firstSelectedId,
          messageId,
          taskSelectionMessagesCount: taskSelectionMessages.length,
        });
        return;
      }

      const selectedDescription =
        taskMessage.taskDescription || taskMessage.text || firstSelectedId;

      const userMessage: Message = {
        id: Math.random().toString(36).substr(2, 9),
        text: selectedDescription,
        sender: 'User',
        timestamp: new Date(),
        isAssistant: false,
        messageType: 'user',
        processed: false,
      };

      // Reuse the standard appendMessage + NEW_MESSAGE pipeline
      appendMessage(
        userMessage,
        currentConversation,
        dispatcherRef.current || undefined
      );

      // Remove all taskSelection messages for this group from the UI
      currentConversation.messages = currentConversation.messages.filter(
        (msg: Message) =>
          !(msg.messageType === 'taskSelection' && msg.parentId === messageId)
      );

      if (typeof triggerConversationUpdate === 'function') {
        triggerConversationUpdate();
      }

      console.log('🔵 [handleContinueSelectionWrapper] Regular task selection processed via confirmation button', {
        messageId,
        firstSelectedId,
      });
      return;
    }

    // From here on we are in the well-defined-task flow
    // CRITICAL: Early duplicate check - if we already have tasks in progress for this wellDefinedTaskId, don't process again
    const dispatcherState = dispatcherRef.current.getCurrentState();
    const wellDefinedTaskId = dispatcherState.context.wellDefinedTaskId;
    console.log('🔵 [handleContinueSelectionWrapper] dispatcherState', dispatcherState);
    console.log('🔵 [handleContinueSelectionWrapper] wellDefinedTaskId', wellDefinedTaskId);

    if (wellDefinedTaskId) {
      console.log('🔵 [handleContinueSelectionWrapper] wellDefinedTaskId exists. Checking for existing tasks for wellDefinedTaskId', wellDefinedTaskId);
      
      // CRITICAL: Check if there's a parent task in option_selection state that needs to be processed
      // This is the task that shows the options - if it exists, we should process the selection
      const parentTaskInOptionSelection = currentConversation.tasks?.find((t: any) => 
        t.wellDefinedTaskId === wellDefinedTaskId &&
        t.dialogueState === 'option_selection'
      );
      
      // Check if there are already tasks created for this wellDefinedTaskId that are beyond option_selection
      const existingProcessedTasks = currentConversation.tasks?.filter((t: any) => 
        t.wellDefinedTaskId === wellDefinedTaskId &&
        t.dialogueState !== 'option_selection' &&
        t.taskId !== wellDefinedTaskId // Not the original parent task ID
      ) || [];
      
      console.log('🔵 [handleContinueSelectionWrapper] Task state check', {
        parentTaskInOptionSelection: parentTaskInOptionSelection ? {
          id: parentTaskInOptionSelection.id,
          taskId: parentTaskInOptionSelection.taskId,
          dialogueState: parentTaskInOptionSelection.dialogueState
        } : null,
        existingProcessedTasksCount: existingProcessedTasks.length,
        existingProcessedTasks: existingProcessedTasks.map((t: any) => ({
          id: t.id,
          taskId: t.taskId,
          dialogueState: t.dialogueState
        }))
      });
      
      // Only exit early if:
      // 1. There's NO parent task in option_selection (meaning it was already processed)
      // 2. AND there are already processed tasks (meaning we've already created tasks from this wellDefinedTaskId)
      // This allows re-processing if the parent task is still in option_selection (user selecting different options)
      if (!parentTaskInOptionSelection && existingProcessedTasks.length > 0) {
        console.warn('🔵 [handleContinueSelectionWrapper] EARLY EXIT - No parent task in option_selection and tasks already exist', {
          wellDefinedTaskId,
          messageId,
          selectedCount: selectedTaskIds.length,
          existingTasksCount: existingProcessedTasks.length,
          existingTasks: existingProcessedTasks.map((t: any) => ({
            id: t.id,
            taskId: t.taskId,
            dialogueState: t.dialogueState,
            isProcessingDialogue: t.isProcessingDialogue
          }))
        });
        console.warn('🔵 [handleContinueSelectionWrapper] This is likely a duplicate call - ignoring');
        return;
      }
      
      // If parent task exists in option_selection, we should process the selection
      // (even if there are existing processed tasks - we might be selecting different options)
      if (parentTaskInOptionSelection) {
        console.log('🔵 [handleContinueSelectionWrapper] Parent task in option_selection found - will process selection', {
          parentTaskId: parentTaskInOptionSelection.id,
          parentTaskTaskId: parentTaskInOptionSelection.taskId
        });
      }
    }

    console.log('🔵 [handleContinueSelectionWrapper] wellDefinedTaskId doesnt exist.')
    // CRITICAL: Clear the parent task that's in option_selection state
    // When user clicks Submit on multi-select options, the parent task (that showed the options)
    // is still marked as processing and in option_selection state. We need to mark it as not processing
    // so we can create new task objects for each selected option.
    const existingActiveTask = currentConversation.tasks?.find((t: any) => t.isProcessingDialogue);
    console.log('🔵 [handleContinueSelectionWrapper] existingActiveTask', existingActiveTask);

    if (existingActiveTask) {
      const isInOptionSelection = existingActiveTask.dialogueState === 'option_selection';
      const isStuck = existingActiveTask.dialogueState === 'defined_task_completed' || 
                      existingActiveTask.dialogueState === 'task_completed' ||
                      existingActiveTask.dialogueState === 'subtask_completed' || // Added per SubtaskClassificationDialogueBlueprint
                      existingActiveTask.isSubmitted;
      
      if (isInOptionSelection || isStuck) {
        console.log('Clearing parent task in option_selection state', existingActiveTask.id, existingActiveTask.dialogueState);
        existingActiveTask.isProcessingDialogue = false;
      }
    }

    // CRITICAL: Check if we've already processed this messageId to prevent duplicate task creation
    // This can happen if handleContinueSelectionWrapper is called multiple times
    const alreadyProcessed = currentConversation.messages.some((msg: Message) => 
      msg.messageType === 'user' && 
      (msg as any).processedMultiSelect && 
      (msg as any).multiSelectParentId === messageId
    );
    
    if (alreadyProcessed) {
      console.warn('🔵 [handleContinueSelectionWrapper] Already processed this selection (messageId check)', { 
        messageId,
        existingMessages: currentConversation.messages
          .filter((msg: Message) => (msg as any).multiSelectParentId === messageId)
          .map((msg: Message) => ({
            id: msg.id,
            text: msg.text,
            messageType: msg.messageType,
            processedMultiSelect: (msg as any).processedMultiSelect
          }))
      });
      return;
    }
    
    console.log('🔵 [handleContinueSelectionWrapper] Not already processed - proceeding', {
      messageId,
      selectedTaskIds,
      totalMessages: currentConversation.messages.length
    });

    // Find the parent message to get task selection messages BEFORE removing them
    const taskSelectionMessages = currentConversation.messages.filter(
      (msg: Message) => msg.messageType === 'taskSelection' && msg.parentId === messageId
    );

    console.log('🔵 [handleContinueSelectionWrapper] Found task selection messages', taskSelectionMessages.length);

    // Get wellDefinedTaskId from context (needed for creating task objects)
    // Note: dispatcherState and wellDefinedTaskId were already retrieved above for duplicate check
    const language = dispatcherState.context.language || 'en';

    // Get subtask data to find option descriptions
    const subtaskData = dispatcherState.context.subtaskData;
    const currentChildren = subtaskData?.currentChildren || [];

    // CRITICAL: Find the parent task object A (created when well-defined task was first selected)
    // This should be the task object with dialogueState 'option_selection' and the same wellDefinedTaskId
    // BUT: If tasks have already been created from this parent (dialogueState is 'leaf_validation'), 
    // we should NOT process again - this prevents duplicate calls
    
    console.log('🔵 [handleContinueSelectionWrapper] Looking for parent task A', {
      wellDefinedTaskId,
      allTasks: currentConversation.tasks?.map((t: any) => ({
        id: t.id,
        taskId: t.taskId,
        wellDefinedTaskId: t.wellDefinedTaskId,
        dialogueState: t.dialogueState,
        isProcessingDialogue: t.isProcessingDialogue
      })) || []
    });
    
    const parentTaskA = currentConversation.tasks?.find((t: any) => 
      t.wellDefinedTaskId === wellDefinedTaskId && 
      t.dialogueState === 'option_selection' &&
      t.taskId === wellDefinedTaskId // Must be the original well-defined task ID, not an option ID
    );

    if (!parentTaskA) {
      // Check if tasks have already been created (parent task A was already processed)
      // Look for tasks that are in leaf_validation or beyond (not option_selection)
      const alreadyProcessedTasks = currentConversation.tasks?.filter((t: any) => 
        t.wellDefinedTaskId === wellDefinedTaskId && 
        t.dialogueState !== 'option_selection' &&
        t.taskId !== wellDefinedTaskId // Not the original parent task ID
      );
      
      // Also check if there's a task in option_selection state but with a different taskId
      // This happens when:
      // 1. A single option was selected - the parent task A was updated in place
      // 2. The selected option has children, so dialogueState is 'option_selection' (not 'leaf_validation')
      // 3. The taskId was updated to the selected option ID (not the original wellDefinedTaskId)
      const taskInOptionSelectionButUpdated = currentConversation.tasks?.find((t: any) => 
        t.wellDefinedTaskId === wellDefinedTaskId && 
        t.dialogueState === 'option_selection' &&
        t.taskId !== wellDefinedTaskId // taskId was updated (single option selected, now has children)
      );
      
      // Check if we're at a deeper level (tasks already processed OR task in option_selection with updated taskId)
      if ((alreadyProcessedTasks && alreadyProcessedTasks.length > 0) || taskInOptionSelectionButUpdated) {
        // CRITICAL: We're at a DEEPER LEVEL of selection (not the first level)
        // Parent task A doesn't exist because we're processing a task that was already created
        // At this point, we should use the reducer's option selection flow, not handleContinueSelectionWrapper
        console.log('🟡 [handleContinueSelectionWrapper] DEEPER LEVEL DETECTED - Using reducer option selection flow', {
          wellDefinedTaskId,
          selectedTaskIds,
          selectedCount: selectedTaskIds.length,
          alreadyProcessedTasksCount: alreadyProcessedTasks.length
        });
        
        // Find the currently processing task (should be in option_selection state)
        let currentlyProcessingTask = currentConversation.tasks?.find((t: any) => 
          t.isProcessingDialogue && t.wellDefinedTaskId === wellDefinedTaskId
        );
        
        if (!currentlyProcessingTask) {
          // Try to find any task in option_selection state for this wellDefinedTaskId
          // This could be the updated parent task A (for single option) or a task waiting for deeper selection
          const taskInOptionSelection = taskInOptionSelectionButUpdated || 
            currentConversation.tasks?.find((t: any) => 
              t.wellDefinedTaskId === wellDefinedTaskId && t.dialogueState === 'option_selection'
            );
          
          if (taskInOptionSelection) {
            console.log('🟡 [handleContinueSelectionWrapper] Found task in option_selection, marking as processing', {
              taskId: taskInOptionSelection.taskId,
              id: taskInOptionSelection.id,
              isUpdatedParent: taskInOptionSelection === taskInOptionSelectionButUpdated
            });
            taskInOptionSelection.isProcessingDialogue = true;
            currentlyProcessingTask = taskInOptionSelection;
          }
        }
        
        if (currentlyProcessingTask) {
          // CRITICAL: For deeper level multi-select, we need to handle it differently:
          // - Single option: Update the existing task and dispatch NEW_MESSAGE (current behavior)
          // - Multiple options: Create/duplicate task objects for each option (like first level)
          
          if (selectedTaskIds.length === 1) {
            // SINGLE OPTION: Update existing task and dispatch NEW_MESSAGE
            const selectedTaskId = selectedTaskIds[0];
            const taskMessage = taskSelectionMessages.find((msg: Message) => msg.taskId === selectedTaskId);
            const optionName = taskMessage?.taskDescription || taskMessage?.text || selectedTaskId;
            
            console.log('🟡 [handleContinueSelectionWrapper] SINGLE deeper level option - updating task and dispatching', {
              selectedTaskId,
              optionName,
              currentTaskId: currentlyProcessingTask.taskId
            });
            
            // Update the task object - the UPDATE_TASK_OBJECT effect will handle parent task removal
            currentlyProcessingTask.taskId = selectedTaskId;
            currentlyProcessingTask.userDescription = optionName;
            currentlyProcessingTask.dialogueState = 'leaf_validation';
            currentlyProcessingTask.isProcessingDialogue = true;
            
            // Create user message
            const userMessage: Message = {
              id: Math.random().toString(36).substr(2, 9),
              text: optionName,
              sender: 'User',
              timestamp: new Date(),
              isAssistant: false,
              messageType: 'user',
              processed: false,
              taskId: selectedTaskId,
              taskDescription: optionName
            };
            
            (userMessage as any).processedMultiSelect = true;
            (userMessage as any).multiSelectParentId = messageId;
            
            console.log('🟢 [handleContinueSelectionWrapper] Adding user message for single deeper option', {
              messageId: userMessage.id,
              text: userMessage.text,
              taskId: userMessage.taskId,
              multiSelectParentId: (userMessage as any).multiSelectParentId
            });
            
            currentConversation.messages.push(userMessage);
            
            // CRITICAL: Trigger UI update BEFORE dispatching to ensure message appears
            if (typeof triggerConversationUpdate === 'function') {
              triggerConversationUpdate();
              console.log('🟢 [handleContinueSelectionWrapper] Triggered conversation update after adding user message');
            }
            
            try {
              console.log('🟪 [handleContinueSelectionWrapper] About to call dispatcher.dispatch(NEW_MESSAGE) for single deeper option', {
                dispatcherExists: !!dispatcherRef.current,
                messageId: userMessage.id,
                messageText: userMessage.text
              });
              await dispatcherRef.current.dispatch({
                type: 'NEW_MESSAGE',
                message: userMessage
              });
              console.log('🟢 [handleContinueSelectionWrapper] Successfully dispatched NEW_MESSAGE for single deeper option');
              console.log('🟪 [handleContinueSelectionWrapper] Dispatcher.dispatch(NEW_MESSAGE) completed successfully for single deeper option');
            } catch (error) {
              console.error('❌ [handleContinueSelectionWrapper] Error dispatching NEW_MESSAGE for deeper level:', error);
              console.error('🟪 [handleContinueSelectionWrapper] Dispatcher.dispatch(NEW_MESSAGE) FAILED for single deeper option', error);
            }
          } else {
            // MULTIPLE OPTIONS: Duplicate the task for each option (like first level)
            console.log('🟡 [handleContinueSelectionWrapper] MULTIPLE deeper level options - duplicating tasks', {
              selectedCount: selectedTaskIds.length,
              currentTaskId: currentlyProcessingTask.taskId
            });
            
            const duplicatedTasks = selectedTaskIds.map((selectedTaskId, index) => {
              const taskMessage = taskSelectionMessages.find((msg: Message) => msg.taskId === selectedTaskId);
              const optionName = taskMessage?.taskDescription || taskMessage?.text || selectedTaskId;
              
              // Create a duplicate of the current task
              const duplicatedTask = {
                ...currentlyProcessingTask,
                id: Math.random().toString(36).substr(2, 9), // New unique ID
                taskId: selectedTaskId, // Update to selected option ID
                userDescription: optionName,
                name: currentlyProcessingTask?.name ? `${currentlyProcessingTask.name}; ${optionName}` : optionName,
                dialogueState: 'leaf_validation' as const,
                isProcessingDialogue: false, // Will be set to true when processing starts
                isSubmitted: false
              };
              
              console.log('➕ [TASK_CREATION] [Chat.tsx] Duplicated task for deeper level option', {
                index: index + 1,
                total: selectedTaskIds.length,
                originalId: currentlyProcessingTask?.id,
                originalTaskId: currentlyProcessingTask?.taskId,
                newId: duplicatedTask.id,
                newTaskId: selectedTaskId,
                optionName,
                wellDefinedTaskId: currentlyProcessingTask?.wellDefinedTaskId
              });
              
              return duplicatedTask;
            });
            
            // Remove the original task (it's being replaced by duplicates)
            // This will be handled via UPDATE_TASK_OBJECT or we can remove it before adding new tasks
            if (currentlyProcessingTask) {
              const originalTaskId = currentlyProcessingTask.id;
              currentConversation.tasks = currentConversation.tasks?.filter((t: any) => t.id !== originalTaskId) || [];
            }
            
            // CRITICAL: Use effect system instead of directly modifying array
            // Dispatch ADD_TASKS_TO_CONVERSATION effect - parent removal will happen in UIEffectsHandler
            if (dispatcherRef.current) {
              const effectBus = dispatcherRef.current.getEffectBus();
              await effectBus.runEffects([{
                type: 'ADD_TASKS_TO_CONVERSATION',
                tasks: duplicatedTasks
              }]);
              
              console.log('➕ [TASK_CREATION] [Chat.tsx] Dispatched ADD_TASKS_TO_CONVERSATION effect for duplicated tasks', {
                duplicatesCreated: duplicatedTasks.length,
                newTaskIds: duplicatedTasks.map((t: any) => t.taskId)
              });
            } else {
              console.error('❌ [Chat.tsx] Cannot dispatch ADD_TASKS_TO_CONVERSATION - dispatcher not available');
            }
            
            // Start processing the first duplicated task
            const firstTask = duplicatedTasks[0];
            if (firstTask) {
              firstTask.isProcessingDialogue = true;
              
              const userMessage: Message = {
                id: Math.random().toString(36).substr(2, 9),
                text: firstTask.userDescription || '',
                sender: 'User',
                timestamp: new Date(),
                isAssistant: false,
                messageType: 'user',
                processed: false,
                taskId: firstTask.taskId,
                taskDescription: firstTask.userDescription
              };
              
              (userMessage as any).processedMultiSelect = true;
              (userMessage as any).multiSelectParentId = messageId;
              
              console.log('🟢 [handleContinueSelectionWrapper] Adding user message for first duplicated task', {
                messageId: userMessage.id,
                text: userMessage.text,
                taskId: userMessage.taskId,
                multiSelectParentId: (userMessage as any).multiSelectParentId
              });
              
              currentConversation.messages.push(userMessage);
              
              // CRITICAL: Trigger UI update BEFORE dispatching to ensure message appears
              if (typeof triggerConversationUpdate === 'function') {
                triggerConversationUpdate();
                console.log('🟢 [handleContinueSelectionWrapper] Triggered conversation update after adding user message for duplicated task');
              }
              
              try {
                console.log('🟪 [handleContinueSelectionWrapper] About to call dispatcher.dispatch(NEW_MESSAGE) for first duplicated task', {
                  dispatcherExists: !!dispatcherRef.current,
                  messageId: userMessage.id,
                  messageText: userMessage.text,
                  taskId: firstTask.taskId
                });
                await dispatcherRef.current.dispatch({
                  type: 'NEW_MESSAGE',
                  message: userMessage
                });
                console.log('🟡 [handleContinueSelectionWrapper] Successfully started processing first duplicated task:', firstTask.taskId);
                console.log('🟪 [handleContinueSelectionWrapper] Dispatcher.dispatch(NEW_MESSAGE) completed successfully for first duplicated task');
              } catch (error) {
                console.error('❌ [handleContinueSelectionWrapper] Error dispatching NEW_MESSAGE for first duplicated task:', error);
                console.error('🟪 [handleContinueSelectionWrapper] Dispatcher.dispatch(NEW_MESSAGE) FAILED for first duplicated task', error);
              }
            }
          }
          
          // Remove task selection messages from UI
          currentConversation.messages = currentConversation.messages.filter((msg: Message) => 
            !(msg.messageType === 'taskSelection' && msg.parentId === messageId)
          );
          
          // Trigger UI update
          if (typeof triggerConversationUpdate === 'function') {
            triggerConversationUpdate();
          }
          
          console.log('🟡 [handleContinueSelectionWrapper] EXIT (deeper level handled via reducer)');
          return;
        } else {
          console.warn('❌ [handleContinueSelectionWrapper] Could not find task to process at deeper level', {
            wellDefinedTaskId,
            availableTasks: currentConversation.tasks?.map((t: any) => ({
              id: t.id,
              taskId: t.taskId,
              dialogueState: t.dialogueState,
              isProcessingDialogue: t.isProcessingDialogue
            })) || []
          });
          return;
        }
      }
      
      console.error('❌ [handleContinueSelectionWrapper] Could not find parent task object A', {
        wellDefinedTaskId,
        availableTasks: currentConversation.tasks?.map((t: any) => ({
          id: t.id,
          taskId: t.taskId,
          wellDefinedTaskId: t.wellDefinedTaskId,
          dialogueState: t.dialogueState,
          isProcessingDialogue: t.isProcessingDialogue
        })) || []
      });
      return;
    }
    
    console.log('🔵 [handleContinueSelectionWrapper] Found parent task A', {
      id: parentTaskA.id,
      taskId: parentTaskA.taskId,
      dialogueState: parentTaskA.dialogueState,
      isProcessingDialogue: parentTaskA.isProcessingDialogue
    });

    console.log('📦 [MULTI-SELECT] Found parent task object A:', {
      id: parentTaskA.id,
      taskId: parentTaskA.taskId,
      userDescription: parentTaskA.userDescription,
      dialogueState: parentTaskA.dialogueState
    });

    // Get option descriptions
    const optionDescriptions: Record<string, string> = {};
    selectedTaskIds.forEach(taskId => {
      const taskMessage = taskSelectionMessages.find((msg: Message) => msg.taskId === taskId);
      let optionName = taskMessage?.taskDescription || taskMessage?.text || taskId;
      
      // Try to get from currentChildren if available
      const child = currentChildren.find((c: any) => c.id === taskId);
      if (child && child.description) {
        optionName = child.description;
      }
      
      optionDescriptions[taskId] = optionName;
    });

    let finalTaskObjects: any[] = [];

    console.log('🔵 [handleContinueSelectionWrapper] Processing selection', {
      selectedCount: selectedTaskIds.length,
      selectedIds: selectedTaskIds,
      optionDescriptions
    });
    
    if (selectedTaskIds.length === 1) {
      // SINGLE OPTION: Update the existing task object A in place
      // No need to remove/add since we're updating the same object reference
      const selectedTaskId = selectedTaskIds[0];
      const optionName = optionDescriptions[selectedTaskId];
      
      console.log('🔵 [handleContinueSelectionWrapper] SINGLE OPTION MODE - Updating parent task A in place', {
        parentTaskAId: parentTaskA.id,
        oldTaskId: parentTaskA.taskId,
        newTaskId: selectedTaskId,
        optionName,
        oldDialogueState: parentTaskA.dialogueState
      });
      
      // Update parentTaskA in place
      parentTaskA.taskId = selectedTaskId;
      parentTaskA.userDescription = optionName;
      parentTaskA.name = optionName;
      parentTaskA.dialogueState = 'leaf_validation';
      parentTaskA.isProcessingDialogue = false; // Will be set to true when processing starts
      
      // For single option, we just update in place - no need to add/remove
      // The object is already in currentConversation.tasks
      finalTaskObjects = [parentTaskA];
      
      console.log('✅ [handleContinueSelectionWrapper] Updated task object A for single option:', {
        id: parentTaskA.id,
        oldTaskId: wellDefinedTaskId,
        newTaskId: selectedTaskId,
        optionName,
        newDialogueState: parentTaskA.dialogueState
      });
    } else {
      // MULTIPLE OPTIONS: Duplicate task object A for each option, then remove original A
      console.log('🔵 [handleContinueSelectionWrapper] MULTIPLE OPTIONS MODE - Duplicating parent task A', {
        parentTaskAId: parentTaskA.id,
        parentTaskATaskId: parentTaskA.taskId,
        selectedCount: selectedTaskIds.length
      });
      
      finalTaskObjects = selectedTaskIds.map((taskId, index) => {
        const optionName = optionDescriptions[taskId];
        
        // Create a duplicate of parentTaskA
        const duplicatedTask = {
          ...parentTaskA,
          id: Math.random().toString(36).substr(2, 9), // New unique ID
          taskId: taskId, // Update to selected option ID
          userDescription: optionName,
          name: optionName,
          finalName: undefined as string | undefined,
          dialogueState: 'leaf_validation' as const,
          isProcessingDialogue: false, // Will be set to true when processing starts
          isSubmitted: false
        };
        
        console.log('✅ [handleContinueSelectionWrapper] Duplicated task object A for option', index + 1, 'of', selectedTaskIds.length, ':', {
          originalId: parentTaskA.id,
          newId: duplicatedTask.id,
          taskId,
          optionName
        });
        
        return duplicatedTask;
      });
      
      // Remove the original parent task A (it's being replaced by duplicates)
      // This will be handled by UIEffectsHandler when ADD_TASKS_TO_CONVERSATION is dispatched
      currentConversation.tasks = currentConversation.tasks?.filter((t: any) => t.id !== parentTaskA.id) || [];
    }
    
    // For single option: Mark parent task A as not processing (it's been updated in place)
    if (selectedTaskIds.length === 1) {
      parentTaskA.isProcessingDialogue = false;
      console.log('🔵 [handleContinueSelectionWrapper] Marked parent task A as not processing (single option mode)');
    }

    if (finalTaskObjects.length === 0) {
      console.warn('No valid task objects created');
      return;
    }

    // CRITICAL: Use effect system instead of directly modifying array
    // For single option: parentTaskA is already in the array (updated in place), so we don't need to add it again
    // For multiple options: dispatch ADD_TASKS_TO_CONVERSATION effect - parent removal will happen in UIEffectsHandler
    const existingTasksCount = (currentConversation.tasks || []).length;
    if (selectedTaskIds.length === 1) {
      // Single option: object already updated in place, just trigger UI update
      console.log('📦 [MULTI-SELECT] Single option - task object updated in place, no need to add again');
    } else {
      // Multiple options: dispatch ADD_TASKS_TO_CONVERSATION effect
      console.log('📦 [MULTI-SELECT] Dispatching ADD_TASKS_TO_CONVERSATION effect', {
        tasksToAdd: finalTaskObjects.length,
        tasksBefore: existingTasksCount,
        duplicatedTasks: finalTaskObjects.map((t: any) => ({
          id: t.id,
          taskId: t.taskId,
          userDescription: t.userDescription,
          dialogueState: t.dialogueState,
          isProcessingDialogue: t.isProcessingDialogue,
          wellDefinedTaskId: t.wellDefinedTaskId
        }))
      });
      
      if (dispatcherRef.current) {
        const effectBus = dispatcherRef.current.getEffectBus();
        await effectBus.runEffects([{
          type: 'ADD_TASKS_TO_CONVERSATION',
          tasks: finalTaskObjects
        }]);
        
        console.log('📦 [MULTI-SELECT] ADD_TASKS_TO_CONVERSATION effect dispatched. Parent removal handled in UIEffectsHandler');
      } else {
        console.error('❌ [Chat.tsx] Cannot dispatch ADD_TASKS_TO_CONVERSATION - dispatcher not available');
      }
    }
    
    const finalTasksCount = currentConversation.tasks?.length || 0;
    console.log('🔵 [handleContinueSelectionWrapper] Final task objects summary:', {
      finalTaskObjectsCount: finalTaskObjects.length,
      tasksBefore: existingTasksCount,
      tasksAfter: finalTasksCount,
      mode: selectedTaskIds.length === 1 ? 'single (updated in place)' : 'multiple (duplicated)',
      finalTasks: finalTaskObjects.map((t: any) => ({
        id: t.id,
        taskId: t.taskId,
        userDescription: t.userDescription,
        dialogueState: t.dialogueState,
        isProcessingDialogue: t.isProcessingDialogue,
        wellDefinedTaskId: t.wellDefinedTaskId
      })),
      allTasksInConversation: currentConversation.tasks?.map((t: any) => ({
        id: t.id,
        taskId: t.taskId,
        dialogueState: t.dialogueState,
        isProcessingDialogue: t.isProcessingDialogue
      })) || []
    });

    // Remove task selection messages from UI
    currentConversation.messages = currentConversation.messages.filter((msg: Message) => 
      !(msg.messageType === 'taskSelection' && msg.parentId === messageId)
    );

    // Trigger UI update
    if (typeof triggerConversationUpdate === 'function') {
      triggerConversationUpdate();
    }

    // Now trigger processing of the first task
    // The existing returnActiveTask mechanism will pick up the first unstarted task
    // We need to dispatch a NEW_MESSAGE for the first task to start its processing
    // This will trigger the reducer to handle it and start the CHECK_SUBTASK_LEAF flow
    
    const firstTask = finalTaskObjects[0];
    if (firstTask) {
      console.log('🔵 [handleContinueSelectionWrapper] Starting processing of first task', {
        taskId: firstTask.taskId,
        taskDescription: firstTask.userDescription,
        taskId_internal: firstTask.id,
        dialogueState: firstTask.dialogueState
      });
      
      // Create a user message for the first task to trigger processing
      const userMessage: Message = {
        id: Math.random().toString(36).substr(2, 9),
        text: firstTask.userDescription,
        sender: 'User',
        timestamp: new Date(),
        isAssistant: false,
        messageType: 'user',
        processed: false,
        taskId: firstTask.taskId,
        taskDescription: firstTask.userDescription
      };
      
      // Mark this message as processed multi-select to prevent duplicate processing
      (userMessage as any).processedMultiSelect = true;
      (userMessage as any).multiSelectParentId = messageId;

      // Add user message to conversation
      currentConversation.messages.push(userMessage);

      // Mark first task as processing (returnActiveTask will handle subsequent ones)
      firstTask.isProcessingDialogue = true;
      
      console.log('🔵 [handleContinueSelectionWrapper] Marked first task as processing', {
        taskId: firstTask.taskId,
        isProcessingDialogue: firstTask.isProcessingDialogue
      });

      // Dispatch NEW_MESSAGE to start processing the first task
      // The reducer will handle it and trigger CHECK_SUBTASK_LEAF
      try {
        console.log('🔵 [handleContinueSelectionWrapper] Dispatching NEW_MESSAGE for first task', {
          messageId: userMessage.id,
          messageText: userMessage.text,
          taskId: userMessage.taskId,
          timestamp: new Date().toISOString()
        });
        console.log('🟪 [handleContinueSelectionWrapper] About to call dispatcher.dispatch(NEW_MESSAGE) for first task', {
          dispatcherExists: !!dispatcherRef.current,
          messageId: userMessage.id
        });
        await dispatcherRef.current.dispatch({
          type: 'NEW_MESSAGE',
          message: userMessage
        });
        console.log('✅ [handleContinueSelectionWrapper] Successfully started processing first task:', firstTask.taskId);
        console.log('🟪 [handleContinueSelectionWrapper] Dispatcher.dispatch(NEW_MESSAGE) completed successfully for first task');
      } catch (error) {
        console.error('❌ [handleContinueSelectionWrapper] Error starting first task processing:', error);
        console.error('🟪 [handleContinueSelectionWrapper] Dispatcher.dispatch(NEW_MESSAGE) FAILED for first task', error);
      }
    } else {
      console.error('❌ [handleContinueSelectionWrapper] No first task to process!');
    }
    
    console.log('🔵 [handleContinueSelectionWrapper] EXIT', {
      messageId,
      finalTaskObjectsCount: finalTaskObjects.length
    });
    console.log('🟥 [handleContinueSelectionWrapper] ========== INDienen BUTTON FLOW END ==========', {
      messageId,
      finalTaskObjectsCount: finalTaskObjects.length,
      timestamp: new Date().toISOString()
    });
  };

  const handleSelectVariantWrapper = async (message: Message, variantId: string) => {
    if (!currentConversation) return;

    // Format variant description using the same logic as VariantSelectorMessage
    let variantDescription = variantId; // Fallback to ID if description can't be found
    
    if (message.variants && message.variants[variantId]) {
      const variantObj = message.variants[variantId] as Record<string, any>;
      
      // Format measurement function (same as in VariantSelectorMessage)
      const formatMeasurement = (key: string, value: any): string => {
        // Handle array of stringified JSON objects
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
          try {
            const parsed = JSON.parse(value[0]);
            return formatMeasurement(key, parsed);
          } catch (e) {
            return `${key}: ${value[0]}`;
          }
        }
        // Special formatting for 'size' (multiple shapes from backend)
        if (key === 'size' && typeof value === 'object' && value !== null) {
          if (Array.isArray(value.values) && value.unit) {
            return `${key}: ${value.values.join(' x ')} ${value.unit}`;
          }
          if ('min' in value || 'max' in value) {
            const min = value.min !== undefined && value.min !== null ? Number(value.min) : undefined;
            const max = value.max !== undefined && value.max !== null ? Number(value.max) : undefined;
            const unit = value.unit || '';
            if (min !== undefined && max !== undefined) {
              if (min === max) return `${key}: ${min} ${unit}`;
              return `${key}: ${min} ${unit} < ${key} < ${max} ${unit}`;
            }
            if (min !== undefined) return `${key}: ${key} > ${min} ${unit}`;
            if (max !== undefined) return `${key}: ${key} < ${max} ${unit}`;
          }
        }
        // Special formatting for 'area', 'height', 'diameter', 'depth', 'length'
        if ((key === 'area' || key === 'height' || key === 'diameter' || key === 'depth' || key === 'length') &&
            typeof value === 'object' && value !== null && ('min' in value || 'max' in value)) {
          const min = value.min !== undefined && value.min !== null ? Number(value.min) : undefined;
          const max = value.max !== undefined && value.max !== null ? Number(value.max) : undefined;
          let unit = value.unit || '';
          if (min !== undefined && max !== undefined) {
            if (min === max) {
              return `${key}: ${min} ${unit}`;
            } else {
              return `${key}: ${min} ${unit} < ${key} < ${max} ${unit}`;
            }
          } else if (min !== undefined) {
            return `${key}: ${key} > ${min} ${unit}`;
          } else if (max !== undefined) {
            return `${key}: ${key} < ${max} ${unit}`;
          }
        }
        // Fallbacks
        if (Array.isArray(value)) {
          return `${key}: ${value.map(v => Array.isArray(v) ? v.join(', ') : v).join(', ')}`;
        } else if (typeof value === 'object' && value !== null) {
          if (!('min' in value) && !('max' in value)) {
            return `${key}: ${JSON.stringify(value)}`;
          } else {
            return '';
          }
        } else {
          return `${key}: ${String(value)}`;
        }
      };
      
      // Compose label from measurements (same as VariantSelectorMessage)
      // Exclude billing fields (billable_unit, quantity_result_obligation, price_per_unit_in_euro)
      const billingFieldsToExclude = ['billable_unit', 'quantity_result_obligation', 'price_per_unit_in_euro'];
      const label = Object.entries(variantObj)
        .filter(([k]) => !billingFieldsToExclude.includes(k))
        .map(([k, v]) => formatMeasurement(k, v))
        .filter(Boolean)
        .join(', ');
      
      if (label) {
        variantDescription = label;
      }
    }

    // Create user message for the selected variant with description (language-dependent via i18n)
    // IMPORTANT: Store the actual variant ID in the message, not just the description
    const userMessage: Message = {
      id: Math.random().toString(36).substr(2, 9),
      text: t(lang, 'selectedVariantLabel', { value: variantDescription }),
      sender: 'User',
      timestamp: new Date(),
      isAssistant: false,
      messageType: 'user',
      // Mark as processed so it doesn't go through LLM classification again
      processed: true,
      variantId: variantId
    };

    // Show the selection in the UI without triggering NEW_MESSAGE logic
    currentConversation.messages.push(userMessage);

    // Drive the dialogue state machine directly via USER_SELECTION
    if (dispatcherRef.current) {
      await dispatcherRef.current.dispatch({
        type: 'USER_SELECTION',
        selection: {
          type: 'variant',
          value: variantId,
          id: variantId
        } as any
      });
    }

    if (typeof triggerConversationUpdate === 'function') {
      triggerConversationUpdate();
    }
  };

  const handleSelectSubtasksWrapper = async (message: Message, selectedSubtaskIds: string[]) => {
    if (!currentConversation) return;

    // Create user message for the selected subtasks (language-dependent via i18n)
    const userMessage: Message = {
      id: Math.random().toString(36).substr(2, 9),
      text: t(lang, 'selectedSubtasksLabel', { value: selectedSubtaskIds.join(', ') }),
          sender: 'User',
          timestamp: new Date(),
          isAssistant: false,
      messageType: 'user',
      processed: false
    };

    // Use appendMessage to add the user message
    appendMessage(userMessage, currentConversation, dispatcherRef.current || undefined);
  };

  // File/drop handler - converts file to text message and uses appendMessage
  const handleFileDrop = (file: File) => {
    if (!currentConversation) return;

    // Convert file to text (placeholder - implement actual conversion)
    const fileText = `File: ${file.name} (${file.size} bytes)`;
    
    const fileMessage: Message = {
      id: Math.random().toString(36).substr(2, 9),
      text: fileText,
      sender: 'User',
      timestamp: new Date(),
      isAssistant: false,
      messageType: 'user',
      processed: false
    };

    // Use appendMessage to add the file message
    appendMessage(fileMessage, currentConversation, dispatcherRef.current || undefined);
  };

  const handleTaskSummaryConfirm = () => {
    if (!currentConversation) {
      return;
    }

    const confirmationText = lang === 'nl' ? 'Ok' : 'Ok';

    const userMessage: Message = {
      id: Math.random().toString(36).substr(2, 9),
      text: confirmationText,
      sender: 'User',
      timestamp: new Date(),
      isAssistant: false,
      messageType: 'user',
      processed: false
    };

    appendMessage(userMessage, currentConversation, dispatcherRef.current || undefined);

    if (typeof triggerConversationUpdate === 'function') {
      triggerConversationUpdate();
    }
  };

  const onUpdateMessage = (messageId: string, updates: Partial<Message>) => {
    if (!currentConversation) return;
    
    // Find and update the message
    const messageIndex = currentConversation.messages.findIndex((m: Message) => m.id === messageId);
    if (messageIndex === -1) return;
    
    // Update the message
    currentConversation.messages[messageIndex] = {
      ...currentConversation.messages[messageIndex],
      ...updates
    };
    
    // If a task was removed, handle removal from task objects
    if (updates.removedTaskId) {
      const removedTaskId = updates.removedTaskId;
      
      // Check if it's a composite ID (for subtasks)
      if (removedTaskId.includes('-')) {
        const [taskId, subtaskId] = removedTaskId.split('-');
        const parentTask = currentConversation.tasks?.find((t: any) => t.id === taskId);
        
        if (parentTask && parentTask.processedSubtasks) {
          // Remove the subtask from processedSubtasks
          parentTask.processedSubtasks = parentTask.processedSubtasks.filter((ps: any) =>
            ps.variantId !== subtaskId &&
            ps.childId !== subtaskId &&
            ps.subtaskId !== subtaskId
          );
          
          // If no subtasks remain, we might want to remove the parent task too
          // But for now, we'll keep the parent task
        }
      } else {
        // Regular task - remove it from conversation.tasks
        if (currentConversation.tasks) {
          currentConversation.tasks = currentConversation.tasks.filter((t: any) => t.id !== removedTaskId);
        }
      }
    }
    
    // If taskData was updated, also update the underlying task objects
    if (updates.taskData && Array.isArray(updates.taskData)) {
      const updatedTaskData = updates.taskData;
      
      // Update task objects based on the updated task data
      updatedTaskData.forEach((taskData: any) => {
        // For regular tasks, the id matches the task object id
        const task = currentConversation.tasks?.find((t: any) => t.id === taskData.id);
        
        if (task) {
          // Update task properties based on edited values
          if (taskData.selectedTask?.description) {
            if (task.selectedTask) {
              task.selectedTask.description = taskData.selectedTask.description;
            } else {
              task.selectedTask = {
                description: taskData.selectedTask.description,
                task_id: task.selectedVariantId || task.id,
                similarity: 1.0
              };
            }
          }
          
          if (taskData.selectedVariantId !== undefined) {
            task.selectedVariantId = taskData.selectedVariantId;
          }
          
          if (taskData.billableAmount !== undefined) {
            task.billableAmount = taskData.billableAmount;
            if (taskData.billableAmount?.unit) {
              task.billableUnit = taskData.billableAmount.unit;
            }
            if (taskData.billableAmount?.amount !== undefined) {
              task.amount = taskData.billableAmount.amount;
            }
          }
          
          if (taskData.employeeCount !== undefined) {
            task.employeeCount = taskData.employeeCount;
          }
          
          if (taskData.location !== undefined) {
            task.location = taskData.location;
          }
        } else {
          // For well-defined tasks with processedSubtasks, we need to find the parent task
          // and update the specific subtask entry
          // The taskData.id for subtasks might be a composite ID like "${task.id}-${displayId}"
          // or just the variantId/childId
          let parentTask: any = null;
          let subtaskIndex = -1;
          
          // First, try to find by composite ID (e.g., "task123-145910")
          if (taskData.id.includes('-')) {
            const [taskId, subtaskId] = taskData.id.split('-');
            parentTask = currentConversation.tasks?.find((t: any) => t.id === taskId);
            if (parentTask && parentTask.processedSubtasks) {
              subtaskIndex = parentTask.processedSubtasks.findIndex((ps: any) =>
                ps.variantId === subtaskId ||
                ps.childId === subtaskId ||
                ps.subtaskId === subtaskId ||
                taskData.selectedVariantId === ps.variantId ||
                taskData.selectedVariantId === ps.childId
              );
            }
          }
          
          // If not found, try to find by matching variantId/childId directly
          if (!parentTask || subtaskIndex === -1) {
            parentTask = currentConversation.tasks?.find((t: any) => 
              t.processedSubtasks && t.processedSubtasks.some((ps: any) => 
                ps.variantId === taskData.id || 
                ps.childId === taskData.id || 
                ps.subtaskId === taskData.id ||
                taskData.selectedVariantId === ps.variantId ||
                taskData.selectedVariantId === ps.childId
              )
            );
            
            if (parentTask && parentTask.processedSubtasks) {
              subtaskIndex = parentTask.processedSubtasks.findIndex((ps: any) =>
                ps.variantId === taskData.id ||
                ps.childId === taskData.id ||
                ps.subtaskId === taskData.id ||
                taskData.selectedVariantId === ps.variantId ||
                taskData.selectedVariantId === ps.childId
              );
            }
          }
          
          if (parentTask && parentTask.processedSubtasks && subtaskIndex >= 0) {
            // Update subtask properties
            if (taskData.selectedVariantId !== undefined) {
              parentTask.processedSubtasks[subtaskIndex].variantId = taskData.selectedVariantId;
            }
            
            if (taskData.billableAmount !== undefined) {
              parentTask.processedSubtasks[subtaskIndex].amount = taskData.billableAmount.amount;
              parentTask.processedSubtasks[subtaskIndex].unit = taskData.billableAmount.unit;
            }
            
            if (taskData.employeeCount !== undefined) {
              // Note: employeeCount is typically at task level, not subtask level
              // But we can store it if needed
            }
            
            if (taskData.location !== undefined) {
              // Location is typically at task level, not subtask level
              // Update the parent task location
              parentTask.location = taskData.location;
            }
            
            // Also update the task description if it was edited
            if (taskData.selectedTask?.description) {
              // For subtasks, we might want to update the description in processedSubtasks
              // but since descriptions come from wellDefinedTasks, we might not want to override them
              // However, if the user explicitly edited it, we should respect that
              // For now, we'll leave it as-is since subtask descriptions are from the well-defined tasks structure
            }
          }
        }
      });
    }
    
    // Trigger conversation update to persist changes
    triggerConversationUpdate();
  };
  
  return (
    <div className="chat">
      <TopBar
        lang={lang}
        setLang={setLang}
        textToSpeechEnabled={textToSpeechEnabled}
        setTextToSpeechEnabled={setTextToSpeechEnabled}
        onLogout={onLogout}
      />
      <div className="chat-vertical-container">
        <MainChat 
          messages={currentConversation?.messages || defaultWelcome}
          messagesEndRef={messagesEndRef}
          formatTime={formatTime}
          lang={lang}
          onSelectTask={handleSelectTaskWrapper}
          onSelectMultipleTasks={handleSelectMultipleTasksWrapper}
          onContinueSelection={handleContinueSelectionWrapper}
          onSelectVariant={handleSelectVariantWrapper}
          onSelectSubtasks={handleSelectSubtasksWrapper}
          currentConversation={currentConversation}
          onUpdateMessage={onUpdateMessage}
          triggerConversationUpdate={triggerConversationUpdate}
          onConfirmTasks={handleTaskSummaryConfirm}
          onWellDefinedTaskSelected={(taskName) => {
            // Create a user message with the selected task name
            if (!currentConversation) return;
            
            const userMessage: Message = {
              id: Math.random().toString(36).substr(2, 9),
              text: taskName,
              sender: 'User',
              timestamp: new Date(),
              isAssistant: false,
              messageType: 'user',
              processed: false
            };
            
            // Use appendMessage to add the message and trigger dialogue logic
            appendMessage(userMessage, currentConversation, dispatcherRef.current || undefined);
          }}
        />
        <InputBar 
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSendMessage={handleSendMessageWrapper}
          lang={lang}
          isSubmitted={currentConversation?.isSubmitted}
          isCancelled={currentConversation?.isCancelled}
        />
      </div>
    </div>
  );
};

export default Chat; 
